/* ==========================================================================
   Fabric Samples
   --------------------------------------------------------------------------
   Four pieces that share one in-memory + localStorage store:

     FabricSamplesStore        the chosen swatches, price tier, cart submit
     <fabric-samples-drawer>   the header "second cart" (extends SideDrawer)
     <fabric-samples-picker>   the search-and-choose modal
     <fabric-samples-button>   the product page button under Add to cart

   Plus the header icon bubble, which is plain DOM that the store updates.

   Talks to:
     - Section Rendering API   (docs.shopify.com/api/section-rendering)
     - Cart AJAX API           (/cart.js, /cart/add.js, /cart/change.js)

   Config comes from #FabricSamplesConfig (rendered by the drawer snippet from
   Theme settings > Fabric Samples). Nothing here is hard-coded to a shop.
   ========================================================================== */
(function () {
  'use strict';

  var RESULTS_SECTION = 'fabric-sample-results';
  var STORAGE_KEY = 'hp:fabric-samples:v2';
  var EVENT_NAME = 'fabric-samples:change';

  /* ---------- tiny helpers ---------------------------------------------- */
  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function q(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  /* ======================================================================
     Store
     ====================================================================== */
  var Store = {
    ready: false,
    config: null,
    selected: [],
    priceTemplate: null,

    init: function () {
      if (this.ready) return true;
      var cfgEl = document.getElementById('FabricSamplesConfig');
      if (!cfgEl) return false;
      this.config = parseJSON(cfgEl, null);
      if (!this.config) return false;
      this.priceTemplate = document.getElementById('FabricSamplesPrices');
      // Coming soon: start empty and ignore anything saved while testing.
      this.selected = this.isComingSoon() ? [] : this.load();
      this.ready = true;
      this.emit();
      return true;
    },

    /* persistence ------------------------------------------------------- */
    load: function () {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr.filter(function (s) { return s && s.id; }) : [];
      } catch (e) {
        return [];
      }
    },

    save: function () {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.selected));
      } catch (e) {
        /* private mode etc. – the in-memory copy still works for this page */
      }
    },

    emit: function () {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { count: this.selected.length } }));
      this.updateHeader();
    },

    /* selection --------------------------------------------------------- */
    has: function (id) {
      id = String(id);
      return this.selected.some(function (s) { return String(s.id) === id; });
    },

    /* Returns true when added, false when already in the set, or the string
       'max' when the merchant's per-order limit has been reached. */
    add: function (item) {
      if (this.isComingSoon()) return false;
      if (!item || !item.id) return false;
      if (this.has(item.id)) return false;
      if (this.atMax()) return 'max';
      this.selected.push({
        id: String(item.id),
        kind: item.kind === 'product' ? 'product' : 'fabric',
        name: item.name || '',
        image: item.image || '',
        product: item.product || '',
        handle: item.handle || '',
      });
      this.save();
      this.emit();
      return true;
    },

    isComingSoon: function () {
      return !!(this.config && this.config.comingSoon);
    },

    atMax: function () {
      var max = Number(this.config.max) || 0;
      return max > 0 && this.selected.length >= max;
    },

    isFree: function () {
      return this.config.pricing === 'free';
    },

    remove: function (id) {
      id = String(id);
      this.selected = this.selected.filter(function (s) { return String(s.id) !== id; });
      this.save();
      this.emit();
    },

    clear: function () {
      this.selected = [];
      this.save();
      this.emit();
    },

    count: function () {
      return this.selected.length;
    },

    /* pricing ----------------------------------------------------------- */
    tierIndex: function () {
      var n = this.selected.length;
      if (n < 1) return 0;
      return Math.min(n, this.config.tiers.length);
    },

    priceFor: function (tier) {
      if (!this.priceTemplate) return '';
      var node = this.priceTemplate.content.querySelector('[data-tier="' + tier + '"]');
      return node ? node.textContent.trim() : '';
    },

    formattedPrice: function () {
      if (this.isFree()) return this.config.labels.free;
      var idx = this.tierIndex();
      return this.priceFor(idx < 1 ? 1 : idx);
    },

    countLabel: function () {
      var n = this.selected.length;
      var l = this.config.labels;
      return n + ' ' + (n === 1 ? l.sampleSingular : l.samplePlural);
    },

    tierVariantId: function () {
      if (this.isFree() && this.config.freeVariantId) return this.config.freeVariantId;
      var tiers = this.config.tiers;
      var idx = Math.min(this.selected.length, tiers.length) - 1;
      if (idx < 0) idx = 0;
      return tiers[idx].id;
    },

    lineQuantity: function () {
      if (this.isFree() && this.config.freeQtyPerSample) return Math.max(1, this.selected.length);
      return 1;
    },

    /* Every variant id that can represent a sample line, in either mode, so
       a re-order always replaces the old line instead of stacking. */
    sampleVariantIds: function () {
      var ids = this.config.tiers.map(function (t) { return String(t.id); });
      if (this.config.freeVariantId) ids.push(String(this.config.freeVariantId));
      return ids;
    },

    buildProperties: function () {
      var props = {};
      var prefix = this.config.labels.propPrefix;
      this.selected.forEach(function (s, i) {
        props[prefix + ' ' + (i + 1)] =
          s.name + (s.product && s.product !== s.name ? ' (' + s.product + ')' : '');
      });
      props['_fabric_handles'] = this.selected.map(function (s) { return s.handle; }).join(',');
      props['_fabric_count'] = String(this.selected.length);
      props['_fabric_pricing'] = this.config.pricing || 'tiered';
      return props;
    },

    /* header icon ------------------------------------------------------- */
    updateHeader: function () {
      var n = this.selected.length;
      var hide = this.config && this.config.hideIconWhenEmpty && n === 0;
      document.querySelectorAll('[data-fs-header-icon]').forEach(function (el) {
        el.classList.toggle('fs-header-icon--hidden', !!hide);
        el.classList.toggle('fs-header-icon--has-items', n > 0);
      });
      document.querySelectorAll('[data-fs-header-count]').forEach(function (el) {
        el.textContent = String(n);
      });
      document.querySelectorAll('[data-fs-header-count-text]').forEach(function (el) {
        el.textContent = n + ' ' + (el.getAttribute('data-fs-header-count-text') || '');
      });
    },

    /* drawer / picker access -------------------------------------------- */
    drawer: function () {
      return document.getElementById('Drawer-Fabric-Samples');
    },

    picker: function () {
      return document.getElementById('FabricSamplesPicker');
    },

    openDrawer: function (triggeredBy) {
      var d = this.drawer();
      if (d && typeof d.open === 'function') d.open(triggeredBy);
    },

    closeDrawer: function () {
      var d = this.drawer();
      if (d && typeof d.close === 'function') d.close();
    },

    openPicker: function (triggeredBy) {
      if (this.isComingSoon()) return this.openDrawer(triggeredBy);
      var p = this.picker();
      if (p && typeof p.openModal === 'function') p.openModal(triggeredBy);
    },

    /* cart -------------------------------------------------------------- */
    submit: async function () {
      if (this.isComingSoon() || !this.selected.length) return;
      var cfg = this.config;
      var tierIds = this.sampleVariantIds();

      // 1. Drop any sample line already in the cart so re-ordering replaces
      //    rather than stacks.
      var cart = await fetch(cfg.routes.cart + '.js', {
        headers: { 'Content-Type': 'application/json' },
      }).then(function (r) { return r.json(); });

      var stale = (cart.items || []).filter(function (item) {
        return tierIds.indexOf(String(item.variant_id)) > -1;
      });

      for (var i = 0; i < stale.length; i++) {
        await fetch(cfg.routes.change + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: stale[i].key, quantity: 0 }),
        });
      }

      // 2. Add the tier variant, one unit, samples as line properties. Ask
      //    for the cart drawer sections at the same time so we can refresh
      //    the main cart without a second round trip.
      var body = {
        items: [
          {
            id: this.tierVariantId(),
            quantity: this.lineQuantity(),
            properties: this.buildProperties(),
          },
        ],
      };

      var cartDrawer = document.querySelector('cart-drawer');
      if (cfg.afterAdd === 'cart_drawer' && cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      var res = await fetch(cfg.routes.add + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      var data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || cfg.labels.addError);
      }

      // 3. Go wherever the merchant asked.
      if (cfg.afterAdd === 'cart_drawer' && cartDrawer && data.sections) {
        this.closeDrawer();
        var self = this;
        setTimeout(function () {
          if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(document.body);
          cartDrawer.renderContents(data);
          self.refreshCartBubble(data);
        }, 250);
        return 'drawer';
      }

      window.location.href = cfg.afterAdd === 'cart' ? cfg.routes.cart : cfg.routes.checkout;
      return 'redirect';
    },

    refreshCartBubble: function (data) {
      if (!data || !data.sections || !data.sections['cart-icon-bubble']) return;
      var html = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
      var inner = html.querySelector('.shopify-section');
      if (!inner) return;
      document.querySelectorAll('.cart-icon-bubble').forEach(function (el) {
        el.innerHTML = inner.innerHTML;
      });
    },
  };

  window.FabricSamplesStore = Store;

  /* ======================================================================
     Header bubble: keep in sync across tabs too
     ====================================================================== */
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY || !Store.ready || Store.isComingSoon()) return;
    Store.selected = Store.load();
    Store.emit();
  });

  /* ======================================================================
     <fabric-samples-picker>
     ====================================================================== */
  class FabricSamplesPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      if (!Store.init()) return;

      this.page = 1;
      this.query = '';
      this.lastFocus = null;

      this.resultsEl = q(this, '[data-fs-results]');
      this.searchInput = q(this, '[data-fs-search]');
      this.clearBtn = q(this, '[data-fs-search-clear]');
      this.countEl = q(this, '[data-fs-modal-count]');
      this.toastEl = q(this, '[data-fs-toast]');

      // The picker sits inside the header section; move it to <body> so it
      // is never trapped behind a sticky/transformed ancestor. Drop any older
      // copy first (the theme editor re-renders the header on every change).
      var self0 = this;
      document.querySelectorAll('fabric-samples-picker').forEach(function (el) {
        if (el !== self0) el.remove();
      });
      if (this.parentElement !== document.body) document.body.appendChild(this);

      this.bind();
      var self = this;
      document.addEventListener(EVENT_NAME, function () { self.renderCount(); self.markChosen(); });
      this.renderCount();
    }

    bind() {
      var self = this;

      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-fs-close]')) {
          self.closeModal();
          return;
        }
        var more = e.target.closest('[data-fs-more]');
        if (more) {
          self.load(self.page + 1, true);
          return;
        }
        var card = e.target.closest('[data-fs-swatch]');
        if (card) self.choose(card);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.hasAttribute('data-open')) self.closeModal(false, true);
      });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'Tab') self.trapFocus(e);
      });

      var run = debounce(function () {
        self.query = self.searchInput.value.trim();
        self.clearBtn.toggleAttribute('data-visible', self.query.length > 0);
        self.load(1, false);
      }, 320);

      this.searchInput.addEventListener('input', run);
      this.searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });

      this.clearBtn.addEventListener('click', function () {
        self.searchInput.value = '';
        self.query = '';
        self.clearBtn.removeAttribute('data-visible');
        self.searchInput.focus();
        self.load(1, false);
      });
    }

    renderCount() {
      if (!this.countEl) return;
      var n = Store.count();
      this.countEl.textContent =
        n === 0 ? Store.config.labels.nothingYet : Store.countLabel() + ' \u00b7 ' + Store.formattedPrice();
    }

    choose(card) {
      var id = card.getAttribute('data-fs-swatch');
      if (Store.has(id)) {
        this.toast(Store.config.labels.already);
        return;
      }
      var result = Store.add({
        id: id,
        kind: card.getAttribute('data-fs-kind'),
        name: card.getAttribute('data-fs-name'),
        image: card.getAttribute('data-fs-image'),
        product: card.getAttribute('data-fs-product'),
        handle: card.getAttribute('data-fs-handle'),
      });
      if (result === 'max') {
        this.toast(Store.config.labels.maxReached);
        return;
      }
      this.markChosen();
      this.toast(Store.config.labels.added);

      // Hand over to the drawer so the shopper sees what they've got.
      var self = this;
      setTimeout(function () {
        self.closeModal(true);
        if (Store.config.openOnAdd) Store.openDrawer(self.lastFocus);
      }, 420);
    }

    markChosen() {
      if (!this.resultsEl) return;
      this.resultsEl.querySelectorAll('[data-fs-swatch]').forEach(function (c) {
        c.toggleAttribute('data-chosen', Store.has(c.getAttribute('data-fs-swatch')));
      });
    }

    openModal(triggeredBy) {
      this.lastFocus = triggeredBy || document.activeElement;
      this.setAttribute('data-open', '');
      document.body.classList.add('fs-modal-open');
      if (!this.loadedOnce) this.load(1, false);
      else this.markChosen();
      this.renderCount();
      var self = this;
      requestAnimationFrame(function () {
        if (window.matchMedia('(min-width: 750px)').matches) self.searchInput.focus();
        else q(self, '[data-fs-close]').focus();
      });
    }

    closeModal(keepFocus, viaKeyboard) {
      this.removeAttribute('data-open');
      document.body.classList.remove('fs-modal-open');
      if (keepFocus || !this.lastFocus || !this.lastFocus.isConnected) return;
      var target = this.lastFocus;
      if (viaKeyboard) {
        // Escape was pressed: wait for the matching keyup before handing focus
        // back, otherwise the drawer's own Escape (keyup) handler closes it too.
        document.addEventListener('keyup', function () { target.focus(); }, { once: true });
      } else {
        target.focus();
      }
    }

    trapFocus(e) {
      var focusables = this.querySelectorAll(
        'button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    toast(msg) {
      if (!this.toastEl) return;
      this.toastEl.textContent = msg;
      this.toastEl.setAttribute('data-visible', '');
      clearTimeout(this._toastT);
      var self = this;
      this._toastT = setTimeout(function () {
        self.toastEl.removeAttribute('data-visible');
      }, 1800);
    }

    /* Results loading (Section Rendering API) --------------------------- */
    buildUrl(page) {
      var cfg = Store.config;
      var url;
      if (this.query) {
        url =
          cfg.searchUrl +
          '?section_id=' +
          RESULTS_SECTION +
          '&type=product&options[prefix]=last&q=' +
          encodeURIComponent(this.query);
      } else {
        url = cfg.browseUrl + '?section_id=' + RESULTS_SECTION;
      }
      if (page > 1) url += '&page=' + page;
      return url;
    }

    load(page, append) {
      var self = this;
      this.page = page;

      if (!append) {
        this.resultsEl.innerHTML =
          '<div class="fs-skeleton">' + new Array(9).join('<div class="fs-skeleton__cell"></div>') + '</div>';
      }

      var token = (this._token = Symbol('req'));

      fetch(this.buildUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          if (self._token !== token) return;
          self.loadedOnce = true;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('[data-fs-results-inner]');
          var cards = inner ? inner.innerHTML : '';
          var more = inner && inner.getAttribute('data-has-next') === 'true';

          var existingMore = self.resultsEl.querySelector('[data-fs-more-wrap]');
          if (existingMore) existingMore.remove();

          if (append) {
            self.resultsEl.querySelector('.fs-results').insertAdjacentHTML('beforeend', cards);
          } else if (!cards.trim()) {
            self.resultsEl.innerHTML = '<p class="fs-status">' + esc(Store.config.labels.noResults) + '</p>';
            return;
          } else {
            self.resultsEl.innerHTML = '<div class="fs-results">' + cards + '</div>';
          }

          if (more) {
            self.resultsEl.insertAdjacentHTML(
              'beforeend',
              '<div data-fs-more-wrap><button type="button" class="fs-more" data-fs-more>' +
                esc(Store.config.labels.loadMore) +
                '</button></div>'
            );
          }
          self.markChosen();
        })
        .catch(function () {
          if (self._token !== token) return;
          self.resultsEl.innerHTML = '<p class="fs-status">' + esc(Store.config.labels.loadError) + '</p>';
        });
    }
  }

  /* ======================================================================
     <fabric-samples-button>  (product page)
     ====================================================================== */
  class FabricSamplesButton extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      if (!Store.init()) return;

      this.current = parseJSON(q(this, '[data-fs-current]'), null);
      this.behaviour = this.getAttribute('data-behaviour') || 'add_current';
      this.button = q(this, '[data-fs-block-button]');
      this.stateEl = q(this, '[data-fs-block-state]');
      this.labelEl = q(this, '[data-fs-block-label]');
      this.addedLabel = this.getAttribute('data-added-label') || 'In your samples';
      this.originalLabel = this.labelEl ? this.labelEl.textContent.trim() : '';

      var self = this;

      if (this.button) {
        this.button.addEventListener('click', function () {
          if (self.behaviour === 'add_current' && self.current) {
            if (Store.has(self.current.id)) {
              // Already in the set: just show the drawer.
              Store.openDrawer(self.button);
              return;
            }
            var result = Store.add(self.current);
            if (result === 'max') {
              // Nothing added; open the drawer so they can see why.
              Store.openDrawer(self.button);
              return;
            }
            self.pulse();
            if (Store.config.openOnAdd) Store.openDrawer(self.button);
          } else if (Store.config.pickerEnabled !== false) {
            Store.openPicker(self.button);
          } else {
            Store.openDrawer(self.button);
          }
        });
      }

      this.querySelectorAll('[data-fs-open-picker]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          Store.openPicker(btn);
        });
      });

      document.addEventListener(EVENT_NAME, function () { self.render(); });
      this.render();
    }

    render() {
      var inSet = !!(this.current && Store.has(this.current.id));
      this.classList.toggle('fs-block--in-set', inSet);
      if (this.stateEl) {
        this.stateEl.toggleAttribute('data-visible', inSet);
      }
      if (this.labelEl && this.behaviour === 'add_current') {
        this.labelEl.textContent = inSet ? this.addedLabel : this.originalLabel;
      }
    }

    pulse() {
      var self = this;
      this.classList.add('fs-block--pulse');
      setTimeout(function () { self.classList.remove('fs-block--pulse'); }, 600);
    }
  }

  /* ======================================================================
     <fabric-samples-drawer>  (header)
     Extends the theme's SideDrawer so the existing <side-drawer-opener>
     in the header can open it, and it animates exactly like the cart.
     ====================================================================== */
  function defineDrawer(Base) {
    class FabricSamplesDrawer extends Base {
      constructor() {
        super();
        this.initialised = false;
      }

      connectedCallback() {
        if (typeof super.connectedCallback === 'function') super.connectedCallback();
        if (this.initialised) return;
        this.initialised = true;
        if (!Store.init()) return;

        var self0 = this;
        document.querySelectorAll('fabric-samples-drawer').forEach(function (el) {
          if (el !== self0) el.remove();
        });

        this.grid = q(this, '[data-fs-grid]');
        this.filledEl = q(this, '[data-fs-filled]');
        this.emptyEl = q(this, '[data-fs-empty]');
        this.footEl = q(this, '[data-fs-foot]');
        this.priceEl = q(this, '[data-fs-price]');
        this.metaEl = q(this, '[data-fs-meta]');
        this.headCount = q(this, '[data-fs-drawer-count]');
        this.cta = q(this, '[data-fs-cta]');
        this.ctaLabel = q(this, '[data-fs-cta-label]');
        this.errorEl = q(this, '[data-fs-error]');
        this.clearBtn = q(this, '[data-fs-clear]');

        var self = this;

        this.addEventListener('click', function (e) {
          var rm = e.target.closest('[data-fs-remove]');
          if (rm) {
            Store.remove(rm.getAttribute('data-fs-remove'));
            return;
          }
          var open = e.target.closest('[data-fs-open-picker]');
          if (open) {
            Store.openPicker(open);
          }
        });

        if (this.cta) {
          this.cta.addEventListener('click', function () { self.submit(); });
        }

        if (this.clearBtn) {
          this.clearBtn.addEventListener('click', function () { Store.clear(); });
        }

        document.addEventListener(EVENT_NAME, function () { self.render(); });
        this.render();
      }

      render() {
        if (!this.grid) return;
        var cfg = Store.config;
        var items = Store.selected;
        var count = items.length;

        var html = '';
        items.forEach(function (item) {
          var isProduct = item.kind === 'product';
          var media = item.image
            ? '<img class="fs-slot__img" src="' + esc(item.image) + '" alt="' + esc(item.name) + '" loading="lazy" width="120" height="120">'
            : '<span class="fs-slot__img fs-slot__img--none" aria-hidden="true"></span>';
          var caption = esc(item.name);
          if (isProduct && item.product && item.product !== item.name) caption += '<small>' + esc(item.product) + '</small>';
          html +=
            '<div class="fs-slot fs-slot--filled' + (isProduct ? ' fs-slot--product' : '') + '">' +
            media +
            '<span class="fs-slot__name">' + caption + '</span>' +
            '<button type="button" class="fs-slot__remove" data-fs-remove="' + esc(item.id) +
            '" aria-label="' + esc(cfg.labels.remove + ' ' + item.name) + '">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
            '</button>' +
            '</div>';
        });
        this.grid.innerHTML = html;

        if (this.filledEl) this.filledEl.hidden = count === 0;
        if (this.emptyEl) this.emptyEl.hidden = count > 0;
        if (this.footEl) this.footEl.hidden = count === 0;

        if (this.priceEl) this.priceEl.textContent = Store.formattedPrice();
        if (this.metaEl) this.metaEl.textContent = count === 0 ? cfg.labels.perSample : Store.countLabel();
        if (this.headCount) this.headCount.textContent = count > 0 ? '(' + count + ')' : '';
        if (this.cta) this.cta.disabled = count === 0;

        var addMore = q(this, '[data-fs-open-picker].fs-add-more');
        if (addMore) {
          var full = Store.atMax();
          addMore.disabled = full;
          addMore.classList.toggle('fs-add-more--full', full);
          addMore.setAttribute('title', full ? cfg.labels.maxReached : '');
        }
      }

      setBusy(state) {
        if (!this.cta) return;
        this.cta.setAttribute('aria-busy', state ? 'true' : 'false');
        this.cta.disabled = state || Store.count() === 0;
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.setAttribute('data-visible', '');
      }

      async submit() {
        if (!Store.count()) return;
        if (this.errorEl) this.errorEl.removeAttribute('data-visible');
        this.setBusy(true);
        try {
          var outcome = await Store.submit();
          if (outcome === 'drawer') this.setBusy(false);
        } catch (err) {
          this.setBusy(false);
          this.showError((err && err.message) || Store.config.labels.addError);
        }
      }
    }
    return FabricSamplesDrawer;
  }

  /* ======================================================================
     Register
     ====================================================================== */
  if (!customElements.get('fabric-samples-picker')) {
    customElements.define('fabric-samples-picker', FabricSamplesPicker);
  }
  if (!customElements.get('fabric-samples-button')) {
    customElements.define('fabric-samples-button', FabricSamplesButton);
  }

  function registerDrawer() {
    if (customElements.get('fabric-samples-drawer')) return;
    var Base = customElements.get('side-drawer') || HTMLElement;
    customElements.define('fabric-samples-drawer', defineDrawer(Base));
  }

  if (customElements.get('side-drawer')) {
    registerDrawer();
  } else {
    // DrawerOpeners.js is deferred too; wait for it so we inherit open/close.
    customElements.whenDefined('side-drawer').then(registerDrawer);
    // Safety net in case that script never loads on some template.
    setTimeout(registerDrawer, 3000);
  }

  // Theme editor: the header (and with it the config) re-renders on every
  // setting change, so re-read it.
  document.addEventListener('shopify:section:load', function () {
    Store.ready = false;
    Store.init();
  });

  // Header bubbles can render before the drawer element upgrades.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Store.init(); });
  } else {
    Store.init();
  }
})();
